import React, { useState, useEffect, useRef } from 'react';
import {
  Mail,
  User,
  Building2,
  Send,
  Loader,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import useGeoLocation from 'react-ipgeolocation';
import { PageLayout } from '../../layouts';
import { FooterSection } from '../../sections';
import { useFormTracking } from '../../hooks/useAnalytics';
import { PriorityBanner } from '../../components/banner/priority-banner';

// Country name to country code mapping for geolocation
const countryNameToCode: { [key: string]: string } = {
  'United States': 'us',
  'United Kingdom': 'gb',
  Canada: 'ca',
  Australia: 'au',
  India: 'in',
  Germany: 'de',
  France: 'fr',
  Italy: 'it',
  Spain: 'es',
  Netherlands: 'nl',
  Belgium: 'be',
  Switzerland: 'ch',
  Austria: 'at',
  Sweden: 'se',
  Norway: 'no',
  Denmark: 'dk',
  Finland: 'fi',
  Brazil: 'br',
  Mexico: 'mx',
  Argentina: 'ar',
  Chile: 'cl',
  Colombia: 'co',
  Peru: 'pe',
  China: 'cn',
  Japan: 'jp',
  'South Korea': 'kr',
  Singapore: 'sg',
  Malaysia: 'my',
  Thailand: 'th',
  Philippines: 'ph',
  Indonesia: 'id',
  Vietnam: 'vn',
  'South Africa': 'za',
  Egypt: 'eg',
  Nigeria: 'ng',
  Kenya: 'ke',
  Russia: 'ru',
  Poland: 'pl',
  'Czech Republic': 'cz',
  Hungary: 'hu',
  Romania: 'ro',
  Bulgaria: 'bg',
  Turkey: 'tr',
  Israel: 'il',
  'United Arab Emirates': 'ae',
  'Saudi Arabia': 'sa',
  Pakistan: 'pk',
  Bangladesh: 'bd',
  'Sri Lanka': 'lk',
  'New Zealand': 'nz',
};

export const ContactPage: React.FC = () => {
  const geoLocation = useGeoLocation();
  const [phoneValue, setPhoneValue] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('in');
  const userHasSelectedCountryRef = useRef(false); // Track manual country selections
  const [submissionState, setSubmissionState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
  });
  const [hasStartedForm, setHasStartedForm] = useState(false);

  // Initialize form tracking
  const {
    trackFormStart,
    trackFieldInteraction,
    trackFormSubmit,
    trackFormSuccess,
    trackFormError,
  } = useFormTracking('contact');

  // Get initial country for phone input
  const getInitialCountry = () => {
    if (geoLocation && 'country' in geoLocation && geoLocation.country) {
      const countryCode = countryNameToCode[(geoLocation as any).country];
      return countryCode || 'in'; // fallback to India
    }
    return 'in'; // default to India
  };

  // Set initial country when geolocation data is available (only if user hasn't manually selected)
  useEffect(() => {
    if (
      geoLocation &&
      'country' in geoLocation &&
      geoLocation.country &&
      !userHasSelectedCountryRef.current
    ) {
      const countryCode = getInitialCountry();
      setSelectedCountry(countryCode);
      console.log('🌍 Contact: Geolocation country applied:', countryCode);
    } else if (userHasSelectedCountryRef.current) {
      console.log(
        '🚫 Contact: Geolocation ignored - user has manually selected country'
      );
    }
  }, [geoLocation]);

  // Track form start on first interaction
  const handleFormStart = () => {
    if (!hasStartedForm) {
      setHasStartedForm(true);
      trackFormStart();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFormStart();
    trackFieldInteraction(e.target.name);

    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackFormSubmit();
    setSubmissionState('loading');

    try {
      const formDataToSubmit = new FormData();

      // Extract phone number and country code from phoneValue
      const fullPhoneNumber = phoneValue || formData.phoneNumber || '';
      const phonePrefix =
        fullPhoneNumber.length > 10 ? fullPhoneNumber.slice(0, -10) : '+91';
      const phoneNumberOnly =
        fullPhoneNumber.length > 10
          ? fullPhoneNumber.slice(-10)
          : fullPhoneNumber;

      // Map form fields to Google Forms entry IDs
      formDataToSubmit.append('entry.393715585', formData.fullName); // name
      formDataToSubmit.append('entry.1373511993', formData.email); // email
      formDataToSubmit.append('entry.2002000935', phonePrefix); // contactNumberPrefix
      formDataToSubmit.append('entry.1285988495', phoneNumberOnly); // contactNumber
      formDataToSubmit.append('entry.1905970013', formData.companyName); // companyName

      // Submit to Google Forms
      await fetch(
        'https://docs.google.com/forms/u/1/d/e/1FAIpQLSeXmAAEoQV9TTP9fLPMu8kmYa-J2HEcehIVYd8sG5VcIg5Phw/formResponse',
        {
          method: 'POST',
          body: formDataToSubmit,
          mode: 'no-cors',
        }
      );

      // Show success state
      setSubmissionState('success');
      trackFormSuccess();

      // Reset form after success
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          companyName: '',
          phoneNumber: '',
        });
        setPhoneValue('');
        setSubmissionState('idle');
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmissionState('error');
      trackFormError('submission_failed');

      // Return to form after showing error
      setTimeout(() => {
        setSubmissionState('idle');
      }, 3000);
    }
  };

  return (
    <PageLayout>
      {/* Mobile-only Priority Access Banner */}
      <div className="hidden max-[599px]:block">
        <PriorityBanner />
      </div>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-32 pb-16 max-[599px]:pt-44">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            {/* Header */}
            <div className="mb-12 text-center">
              <h1 className="mb-6 text-4xl font-bold text-gray-900 sm:text-5xl">
                Contact Us
              </h1>
              <p className="text-xl leading-relaxed text-gray-600">
                Get in touch with our team. We'd love to hear from you and
                discuss how DataWyse can transform your data analytics.
              </p>
            </div>

            {/* Contact Form */}
            <div className="rounded-3xl bg-white p-8 shadow-xl sm:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Full Name Field */}
                <div>
                  <label
                    htmlFor="fullName"
                    className="mb-2 block text-sm font-semibold text-gray-900"
                  >
                    Full Name *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="block w-full rounded-xl border border-gray-300 py-4 pr-3 pl-10 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-gray-900"
                  >
                    Email ID *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="ch-no-capture block w-full rounded-xl border border-gray-300 py-4 pr-3 pl-10 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                {/* Company Name Field */}
                <div>
                  <label
                    htmlFor="companyName"
                    className="mb-2 block text-sm font-semibold text-gray-900"
                  >
                    Company Name *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      className="block w-full rounded-xl border border-gray-300 py-4 pr-3 pl-10 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter your company name"
                    />
                  </div>
                </div>

                {/* Contact Number - PhoneInput Component */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-900">
                    Contact Number *
                  </label>
                  <PhoneInput
                    country={selectedCountry}
                    value={phoneValue}
                    onChange={(phone, countryData) => {
                      handleFormStart();
                      trackFieldInteraction('phoneNumber');

                      setPhoneValue(phone);
                      setFormData(prev => ({
                        ...prev,
                        phoneNumber: phone,
                      }));

                      // Track country changes - both manual dropdown and auto-detection
                      if (countryData && 'countryCode' in countryData) {
                        const newCountry = (
                          countryData as any
                        ).countryCode.toLowerCase();
                        if (newCountry !== selectedCountry) {
                          console.log(
                            '👤 Contact: Country change detected:',
                            selectedCountry,
                            '->',
                            newCountry
                          );
                          userHasSelectedCountryRef.current = true;
                          setSelectedCountry(newCountry);
                        }
                      }
                    }}
                    placeholder="Enter phone number"
                    enableSearch={true}
                    searchPlaceholder="Search countries..."
                    countryCodeEditable={true}
                    inputClass="ch-no-capture"
                    containerStyle={{
                      width: '100%',
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '56px',
                      fontSize: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      paddingLeft: '60px',
                      backgroundColor: 'white',
                      color: '#111827',
                      transition: 'all 0.2s',
                    }}
                    buttonStyle={{
                      border: '1px solid #d1d5db',
                      borderRadius: '12px 0 0 12px',
                      backgroundColor: 'white',
                      height: '56px',
                    }}
                    dropdownStyle={{
                      maxHeight: '200px',
                      borderRadius: '12px',
                      boxShadow:
                        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }}
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submissionState === 'loading'}
                    className="flex w-full transform items-center justify-center rounded-xl bg-[rgb(59,91,235)] px-8 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:bg-[rgb(79,111,255)] hover:shadow-xl focus:ring-2 focus:ring-[rgb(59,91,235)]/50 focus:ring-offset-2 focus:outline-none disabled:opacity-70 disabled:hover:scale-100 disabled:hover:bg-[rgb(59,91,235)]"
                  >
                    {submissionState === 'loading' ? (
                      <>
                        <Loader className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : submissionState === 'success' ? (
                      <>
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Message Sent!
                      </>
                    ) : submissionState === 'error' ? (
                      <>
                        <XCircle className="mr-2 h-5 w-5" />
                        Submission Failed
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Submit
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Contact Information */}
              <div className="mt-12 border-t border-gray-200 pt-8">
                <div className="flex justify-center">
                  <div className="flex flex-col items-center">
                    <Mail className="mb-3 h-8 w-8 text-blue-600" />
                    <p className="text-lg text-gray-600">
                      Feel Free to drop us an email at :
                      <a
                        href="mailto:info@datawyse.in"
                        className="ml-1 text-blue-600 transition-colors duration-200 hover:text-blue-700 hover:underline"
                      >
                        info@datawyse.in
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </PageLayout>
  );
};
